import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import type { AdminMaterial } from '@/mocks/adminData';
import { getTree, addCategory, updateCategory, deleteCategory } from '@/api/materialCategory';
import type { CategoryNode } from '@/api/materialCategory';
import { listMaterial, deleteMaterial, toggleDownload, toggleConfidential, saveConfidentialAgents } from '@/api/materialInfo';
import { listAgent } from '@/api/agent';
import type { UserInfo } from '@/api/agent';
import type { MaterialItem } from '@/api/materialInfo';

const ELEARNING = 'E-learning';

/** 文件类型对应缩略图占位图标（参考详情页 officeIconMap） */
const typeThumbnailConfig: Record<string, { icon: string; color: string; bg: string }> = {
  Video: { icon: 'ri-video-line',        color: '#6B7280', bg: '#F3F4F6' },
  PDF:   { icon: 'ri-file-pdf-2-line',   color: '#DC2626', bg: '#FEF2F2' },
  PPT:   { icon: 'ri-file-ppt-2-line',   color: '#D04423', bg: '#FFF0ED' },
  Excel: { icon: 'ri-file-excel-2-line', color: '#1E7E45', bg: '#EDFAF2' },
  Word:  { icon: 'ri-file-word-2-line',  color: '#1A5CA8', bg: '#EDF3FC' },
};

function isDownloadLocked(m: AdminMaterial): boolean {
  if (m.category === ELEARNING) return true;
  return false;
}

function TypeBadge({ type }: { type: AdminMaterial['type'] }) {
  const iconMap: Record<string, string> = {
    Video: 'ri-video-line',
    PPT: 'ri-presentation-line',
    Excel: 'ri-file-excel-line',
    Word: 'ri-file-word-line',
    PDF: 'ri-file-pdf-line',
  };
  const icon = iconMap[type] ?? 'ri-file-line';
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border whitespace-nowrap bg-gray-50 text-gray-700 border-gray-200">
      <i className={`${icon} text-xs`}></i>
      {type}
    </span>
  );
}

function ConfidentialBadge({ confidential }: { confidential?: boolean }) {
  if (confidential) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-red-50 text-red-600 border-red-200 whitespace-nowrap">
        <i className="ri-shield-keyhole-line text-xs"></i>机密
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-green-50 text-green-600 border-green-200 whitespace-nowrap">
      <i className="ri-global-line text-xs"></i>普通
    </span>
  );
}

function ConfidentialAgentsModal({
  material,
  onClose,
  onSave,
}: {
  material: AdminMaterial;
  onClose: () => void;
  onSave: (materialId: string, agentIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(material.confidentialAgents ?? []);
  const [agents, setAgents] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const result = await listAgent({ pageSize: 9999 });
        setAgents(result.list ?? []);
      } catch (err) {
        console.error('加载代理商列表失败', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id: string) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const filtered = search.trim()
    ? agents.filter((a) => a.name.includes(search) || (a.company && a.company.includes(search)) || (a.loginname && a.loginname.includes(search)))
    : agents;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">机密权限管理</h3>
            <p className="text-xs text-gray-400 mt-0.5 max-w-[280px] truncate">{material.title}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-4" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
            <i className="ri-shield-keyhole-line text-sm text-red-500 shrink-0"></i>
            <p className="text-xs text-red-700 leading-relaxed">
              此为机密资料，仅勾选的代理商可查看。已选 <strong>{selected.length}</strong> 人。
            </p>
          </div>
          {/* 搜索 */}
          <div className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索代理商名称、公司或邮箱..."
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400"
            />
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <i className="ri-loader-4-line animate-spin text-gray-400 text-lg"></i>
                <span className="ml-2 text-sm text-gray-400">加载代理商列表...</span>
              </div>
            ) : (
              <>
                {filtered.map((agent) => {
                  const checked = selected.includes(agent.id);
                  return (
                    <label key={agent.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${checked ? 'border-red-200 bg-red-50/40' : 'border-gray-100 bg-gray-50/50 hover:bg-gray-100/60'}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(agent.id)} className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-red-500" />
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: '#0069BD' }}>
                        {agent.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{agent.name}</p>
                        <p className="text-xs text-gray-400 truncate">
                          <span className="text-gray-500 font-medium">{agent.loginname}</span>
                          <span className="mx-1">·</span>
                          {agent.company || '-'}{agent.region ? ` · ${agent.region}` : ''}
                        </p>
                      </div>
                      {checked && <i className="ri-checkbox-circle-line text-sm text-red-500 shrink-0"></i>}
                    </label>
                  );
                })}
                {filtered.length === 0 && !loading && <p className="text-sm text-gray-400 text-center py-8">{search ? '无匹配代理商' : '暂无代理商'}</p>}
              </>
            )}
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
          <button onClick={() => { onSave(material.id, selected); onClose(); }} className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap" style={{ background: '#0069BD' }}>保存权限</button>
        </div>
      </div>
    </div>
  );
}

function CategoryManagerModal({
  onClose,
  materials,
}: {
  onClose: () => void;
  materials: AdminMaterial[];
}) {
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPrimary, setExpandedPrimary] = useState<string | null>(null);
  const [expandedSub, setExpandedSub] = useState<string | null>(null);

  // Primary editing / deleting
  const [editingPrimary, setEditingPrimary] = useState<{ id: string; label: string; icon: string; color: string } | null>(null);
  const [deletingPrimary, setDeletingPrimary] = useState<string | null>(null);
  const [newPrimary, setNewPrimary] = useState<{ label: string; icon: string; color: string }>({ label: '', icon: 'ri-folder-line', color: '#0069BD' });

  // Sub editing / deleting
  const [editingSub, setEditingSub] = useState<{ primaryId: string; subId: string; value: string } | null>(null);
  const [deletingSub, setDeletingSub] = useState<{ primaryId: string; subId: string } | null>(null);
  const [newSubName, setNewSubName] = useState('');
  const [newSubPrimary, setNewSubPrimary] = useState('');

  // Model editing / deleting
  const [editingModel, setEditingModel] = useState<{ primaryId: string; subId: string; modelId: string; value: string } | null>(null);
  const [deletingModel, setDeletingModel] = useState<{ primaryId: string; subId: string; modelId: string } | null>(null);
  const [newModelName, setNewModelName] = useState<Record<string, string>>({});

  // 从后端加载分类树
  async function loadTree() {
    try {
      setLoading(true);
      const data = await getTree();
      setTree(data);
      // 自动展开第一个一级分类
      if (data.length > 0 && !expandedPrimary) {
        setExpandedPrimary(data[0].id);
      }
    } catch (err) {
      console.error('加载分类树失败', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTree();
  }, []);



  /* ── Primary ── */
  const handleAddPrimary = async () => {
    const trimmed = newPrimary.label.trim();
    if (!trimmed) return;
    if (tree.some((p) => p.label === trimmed)) return;
    try {
      await addCategory({ name: trimmed, level: 1, icon: newPrimary.icon, color: newPrimary.color });
      setNewPrimary({ label: '', icon: 'ri-folder-line', color: '#0069BD' });
      await loadTree();
    } catch (err) {
      console.error('新增一级分类失败', err);
    }
  };

  const handleSavePrimaryEdit = async () => {
    if (!editingPrimary) return;
    const trimmed = editingPrimary.label.trim();
    if (!trimmed) return;
    try {
      await updateCategory({ id: editingPrimary.id, name: trimmed, icon: editingPrimary.icon, color: editingPrimary.color });
      setEditingPrimary(null);
      await loadTree();
    } catch (err) {
      console.error('更新一级分类失败', err);
    }
  };

  const handleDeletePrimary = async () => {
    if (!deletingPrimary) return;
    try {
      await deleteCategory(deletingPrimary);
      setDeletingPrimary(null);
      if (expandedPrimary === deletingPrimary) setExpandedPrimary(null);
      await loadTree();
    } catch (err) {
      console.error('删除一级分类失败', err);
    }
  };

  /* ── Sub ── */
  const handleAddSub = async () => {
    const trimmed = newSubName.trim();
    if (!trimmed || !newSubPrimary) return;
    const primary = tree.find((p) => p.id === newSubPrimary);
    if (primary && primary.subCategories.some((s) => s.label === trimmed)) return;
    try {
      await addCategory({ name: trimmed, level: 2, parentId: newSubPrimary, icon: 'ri-folder-line' });
      setNewSubName('');
      await loadTree();
    } catch (err) {
      console.error('新增二级分类失败', err);
    }
  };

  const handleSaveSubEdit = async () => {
    if (!editingSub) return;
    const trimmed = editingSub.value.trim();
    if (!trimmed) return;
    try {
      await updateCategory({ id: editingSub.subId, name: trimmed });
      setEditingSub(null);
      await loadTree();
    } catch (err) {
      console.error('更新二级分类失败', err);
    }
  };

  const handleDeleteSub = async () => {
    if (!deletingSub) return;
    try {
      await deleteCategory(deletingSub.subId);
      setDeletingSub(null);
      await loadTree();
    } catch (err) {
      console.error('删除二级分类失败', err);
    }
  };

  /* ── Model ── */
  const handleAddModel = async (primaryId: string, subId: string) => {
    const key = `${primaryId}__${subId}`;
    const trimmed = (newModelName[key] ?? '').trim();
    if (!trimmed) return;
    const primary = tree.find((p) => p.id === primaryId);
    const sub = primary?.subCategories.find((s) => s.id === subId);
    if (sub && sub.models.some((m) => m.label === trimmed)) return;
    try {
      await addCategory({ name: trimmed, level: 3, parentId: subId });
      setNewModelName((prev) => ({ ...prev, [key]: '' }));
      await loadTree();
    } catch (err) {
      console.error('新增型号失败', err);
    }
  };

  const handleSaveModelEdit = async () => {
    if (!editingModel) return;
    const trimmed = editingModel.value.trim();
    if (!trimmed) return;
    try {
      await updateCategory({ id: editingModel.modelId, name: trimmed });
      setEditingModel(null);
      await loadTree();
    } catch (err) {
      console.error('更新型号失败', err);
    }
  };

  const handleDeleteModel = async () => {
    if (!deletingModel) return;
    try {
      await deleteCategory(deletingModel.modelId);
      setDeletingModel(null);
      await loadTree();
    } catch (err) {
      console.error('删除型号失败', err);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-base font-bold text-gray-900">管理分类结构</h3>
            <p className="text-xs text-gray-400 mt-0.5">维护一级 / 二级 / 三级（产品型号）分类</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
              <span className="ml-2 text-sm text-gray-400">加载分类数据...</span>
            </div>
          )}
          {/* Add Primary */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">新增一级分类</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newPrimary.label}
                onChange={(e) => setNewPrimary((prev) => ({ ...prev, label: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPrimary(); } }}
                placeholder="一级分类名称..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white"
              />
              <button
                type="button"
                onClick={handleAddPrimary}
                disabled={!newPrimary.label.trim()}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-add-line mr-1"></i>创建
              </button>
            </div>
          </div>

          {/* Add Sub */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">新增二级分类</p>
            <div className="flex gap-2">
              <div className="relative w-40 shrink-0">
                <select
                  value={newSubPrimary}
                  onChange={(e) => setNewSubPrimary(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer"
                >
                  <option value="">选择一级分类</option>
                  {tree.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-arrow-down-s-line text-base"></i>
                </div>
              </div>
              <input
                type="text"
                value={newSubName}
                onChange={(e) => setNewSubName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSub(); } }}
                placeholder="二级分类名称..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400"
              />
              <button
                type="button"
                onClick={handleAddSub}
                disabled={!newSubName.trim() || !newSubPrimary}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-add-line mr-1"></i>创建
              </button>
            </div>
          </div>

          {/* Tree */}
          <div className="space-y-3">
            {tree.map((primary) => (
              <div key={primary.id} className="border border-gray-100 rounded-xl overflow-hidden">
                {/* Primary header */}
                {editingPrimary?.id === primary.id ? (
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">编辑一级分类</p>
                    <div className="flex gap-2">
                      <input
                        autoFocus
                        type="text"
                        value={editingPrimary.label}
                        onChange={(e) => setEditingPrimary({ ...editingPrimary, label: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSavePrimaryEdit(); if (e.key === 'Escape') setEditingPrimary(null); }}
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:border-gray-500 bg-white"
                      />
                      <button type="button" onClick={handleSavePrimaryEdit} className="w-7 h-7 flex items-center justify-center rounded-md text-green-600 hover:bg-green-50 cursor-pointer"><i className="ri-check-line text-sm"></i></button>
                      <button type="button" onClick={() => setEditingPrimary(null)} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-sm"></i></button>
                    </div>
                  </div>
                ) : deletingPrimary === primary.id ? (
                  <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex items-center gap-2">
                    <span className="flex-1 text-sm text-red-700 font-medium">删除「{primary.label}」及 {primary.subCategories.length} 个二级分类？</span>
                    <button type="button" onClick={handleDeletePrimary} className="px-3 py-1.5 bg-red-500 text-white rounded-md text-xs font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">删除</button>
                    <button type="button" onClick={() => setDeletingPrimary(null)} className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-md text-xs font-semibold hover:bg-gray-100 cursor-pointer whitespace-nowrap">取消</button>
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedPrimary(expandedPrimary === primary.id ? null : primary.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedPrimary(expandedPrimary === primary.id ? null : primary.id); } }}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left group/primary"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${primary.color}18` }}>
                      <i className={`${primary.icon} text-sm`} style={{ color: primary.color }}></i>
                    </div>
                    <span className="flex-1 text-sm font-bold text-gray-800">{primary.label}</span>
                    <span className="text-xs text-gray-400 mr-1">{primary.subCategories.length} 个二级分类</span>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover/primary:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditingPrimary({ id: primary.id, label: primary.label, icon: primary.icon, color: primary.color }); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer"
                      >
                        <i className="ri-pencil-line text-xs"></i>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeletingPrimary(primary.id); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                    {expandedPrimary === primary.id ? <i className="ri-arrow-up-s-line text-gray-400 text-base shrink-0"></i> : <i className="ri-arrow-down-s-line text-gray-400 text-base shrink-0"></i>}
                  </div>
                )}

                {expandedPrimary === primary.id && (
                  <div className="divide-y divide-gray-50">
                    {primary.subCategories.map((sub) => (
                      <div key={sub.id} className="bg-white">
                        <div className="flex items-center gap-2 px-4 py-2.5 group/sub">
                          <div className="w-4 h-4 flex items-center justify-center shrink-0 ml-4">
                            <i className="ri-corner-down-right-line text-xs text-gray-300"></i>
                          </div>
                          {editingSub?.primaryId === primary.id && editingSub?.subId === sub.id ? (
                            <>
                              <input
                                autoFocus
                                type="text"
                                value={editingSub.value}
                                onChange={(e) => setEditingSub({ ...editingSub, value: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSubEdit(); if (e.key === 'Escape') setEditingSub(null); }}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:border-gray-500 bg-white"
                              />
                              <button type="button" onClick={handleSaveSubEdit} className="w-7 h-7 flex items-center justify-center rounded-md text-green-600 hover:bg-green-50 cursor-pointer"><i className="ri-check-line text-sm"></i></button>
                              <button type="button" onClick={() => setEditingSub(null)} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-sm"></i></button>
                            </>
                          ) : deletingSub?.primaryId === primary.id && deletingSub?.subId === sub.id ? (
                            <>
                              <span className="flex-1 text-sm text-red-600 font-medium">删除「{sub.label}」及其所有型号？</span>
                              <button type="button" onClick={handleDeleteSub} className="px-2.5 py-1 bg-red-500 text-white rounded-md text-xs font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">删除</button>
                              <button type="button" onClick={() => setDeletingSub(null)} className="px-2.5 py-1 border border-gray-200 text-gray-600 rounded-md text-xs font-semibold hover:bg-gray-100 cursor-pointer whitespace-nowrap">取消</button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => setExpandedSub(expandedSub === `${primary.id}__${sub.id}` ? null : `${primary.id}__${sub.id}`)}
                                className="flex items-center gap-2 flex-1 text-left cursor-pointer"
                              >
                                <i className={`${sub.icon} text-xs text-gray-400`}></i>
                                <span className="text-sm font-semibold text-gray-700">{sub.label}</span>
                                <span className="text-xs text-gray-400 ml-1">
                                  {sub.models.length > 0 ? `${sub.models.length} 个型号` : '无型号'}
                                </span>
                                {expandedSub === `${primary.id}__${sub.id}` ? <i className="ri-arrow-up-s-line text-gray-300 text-sm ml-auto"></i> : <i className="ri-arrow-down-s-line text-gray-300 text-sm ml-auto"></i>}
                              </button>
                              <button type="button" onClick={() => setEditingSub({ primaryId: primary.id, subId: sub.id, value: sub.label })} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer opacity-0 group-hover/sub:opacity-100 transition-opacity"><i className="ri-pencil-line text-xs"></i></button>
                              <button type="button" onClick={() => setDeletingSub({ primaryId: primary.id, subId: sub.id })} className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer opacity-0 group-hover/sub:opacity-100 transition-opacity"><i className="ri-delete-bin-line text-xs"></i></button>
                            </>
                          )}
                        </div>

                        {expandedSub === `${primary.id}__${sub.id}` && (
                          <div className="bg-gray-50/60 border-t border-gray-100 px-4 py-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide ml-8">产品型号（三级分类）</p>
                            <div className="space-y-1 ml-8">
                              {sub.models.map((model) => (
                                <div key={model.id} className="flex items-center gap-2 group/mdl">
                                  {editingModel?.primaryId === primary.id && editingModel?.subId === sub.id && editingModel?.modelId === model.id ? (
                                    <>
                                      <input
                                        autoFocus
                                        type="text"
                                        value={editingModel.value}
                                        onChange={(e) => setEditingModel({ ...editingModel, value: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveModelEdit(); if (e.key === 'Escape') setEditingModel(null); }}
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:border-gray-500 bg-white"
                                      />
                                      <button type="button" onClick={handleSaveModelEdit} className="w-6 h-6 flex items-center justify-center rounded-md text-green-600 hover:bg-green-50 cursor-pointer"><i className="ri-check-line text-xs"></i></button>
                                      <button type="button" onClick={() => setEditingModel(null)} className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
                                    </>
                                  ) : deletingModel?.primaryId === primary.id && deletingModel?.subId === sub.id && deletingModel?.modelId === model.id ? (
                                    <>
                                      <span className="flex-1 text-xs text-red-600 font-medium">删除「{model.label}」？</span>
                                      <button type="button" onClick={handleDeleteModel} className="px-2 py-0.5 bg-red-500 text-white rounded text-xs font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">删除</button>
                                      <button type="button" onClick={() => setDeletingModel(null)} className="px-2 py-0.5 border border-gray-200 text-gray-600 rounded text-xs font-semibold hover:bg-gray-100 cursor-pointer whitespace-nowrap">取消</button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="flex-1 text-xs text-gray-700 font-medium">{model.label}</span>
                                      <span className="text-xs text-gray-400 mr-1">
                                        {materials.filter((m) => m.modelCategory === model.id).length} 份资料
                                      </span>
                                      <button type="button" onClick={() => setEditingModel({ primaryId: primary.id, subId: sub.id, modelId: model.id, value: model.label })} className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer opacity-0 group-hover/mdl:opacity-100 transition-opacity"><i className="ri-pencil-line text-xs"></i></button>
                                      <button type="button" onClick={() => setDeletingModel({ primaryId: primary.id, subId: sub.id, modelId: model.id })} className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer opacity-0 group-hover/mdl:opacity-100 transition-opacity"><i className="ri-delete-bin-line text-xs"></i></button>
                                    </>
                                  )}
                                </div>
                              ))}
                              {sub.models.length === 0 && (
                                <p className="text-xs text-gray-400 italic">暂无型号，在下方添加</p>
                              )}
                            </div>
                            <div className="flex gap-2 ml-8 mt-2">
                              <input
                                type="text"
                                value={newModelName[`${primary.id}__${sub.id}`] ?? ''}
                                onChange={(e) => setNewModelName((prev) => ({ ...prev, [`${primary.id}__${sub.id}`]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddModel(primary.id, sub.id); } }}
                                placeholder="新型号名称..."
                                className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:border-gray-400 bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddModel(primary.id, sub.id)}
                                disabled={!(newModelName[`${primary.id}__${sub.id}`] ?? '').trim()}
                                className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs font-semibold hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors"
                              >
                                <i className="ri-add-line mr-0.5"></i>添加型号
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {primary.subCategories.length === 0 && (
                      <div className="px-4 py-4 text-center text-xs text-gray-400">暂无二级分类</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 shrink-0">
          <button type="button" onClick={onClose} className="w-full py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">完成</button>
        </div>
      </div>
    </div>
  );
}

export default function MaterialsAdminPage() {
  const navigate = useNavigate();
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [materials, setMaterials] = useState<AdminMaterial[]>([]);
  const [total, setTotal] = useState(0);
  const [countAll, setCountAll] = useState(0);
  const [countNormal, setCountNormal] = useState(0);
  const [countConfidential, setCountConfidential] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filterCat, setFilterCat] = useState('all');
  const [filterLevel, setFilterLevel] = useState<'all' | 'normal' | 'confidential'>('all');
  const [filterPrimary, setFilterPrimary] = useState('all');
  const [filterModel, setFilterModel] = useState('all');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // 按选中一级分类过滤二级分类（用于层级筛选下拉框）
  const filteredSubCategories = useMemo(() => {
    if (!tree.length) return [];
    if (filterPrimary === 'all') {
      return tree.flatMap((p) =>
        p.subCategories.map((s) => ({ ...s, primaryLabel: p.label, primaryColor: p.color }))
      );
    }
    const primary = tree.find((p) => p.id === filterPrimary);
    if (!primary) return [];
    return primary.subCategories.map((s) => ({
      ...s,
      primaryLabel: primary.label,
      primaryColor: primary.color,
    }));
  }, [tree, filterPrimary]);

  // 按选中二级分类获取三级型号列表
  const filteredModels = useMemo(() => {
    if (filterCat === 'all') return [];
    for (const p of tree) {
      for (const s of p.subCategories) {
        if (s.id === filterCat) return s.models;
      }
    }
    return [];
  }, [tree, filterCat]);

  const [confModal, setConfModal] = useState<AdminMaterial | null>(null);
  const [showCatManager, setShowCatManager] = useState(false);

  // 记录加载失败的缩略图 ID，用于显示类型占位图标
  const [brokenThumbnails, setBrokenThumbnails] = useState<Set<string>>(new Set());
  const handleThumbnailError = (id: string) => {
    setBrokenThumbnails((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // —— Toast ——
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toAdminMaterial = useCallback((item: MaterialItem): AdminMaterial => ({
    id: item.id,
    title: item.title,
    type: item.type as AdminMaterial['type'],
    pptType: item.pptType as AdminMaterial['pptType'],
    primaryCategory: item.primaryCategory,
    category: item.category,
    modelCategory: item.modelCategory,
    fileSize: item.fileSize,
    uploadDate: typeof item.uploadDate === 'number' && item.uploadDate > 0 ? new Date(item.uploadDate).toISOString().split('T')[0] : (item.uploadDate || ''),
    uploadedBy: item.uploadedBy,
    views: item.views,
    downloads: item.downloads,
    previewAllowed: item.previewAllowed,
    downloadAllowed: item.downloadAllowed,
    thumbnail: item.thumbnail,
    isVideo: item.isVideo,
    videoDuration: item.videoDuration,
    videoUrl: item.fileUrl,
    description: item.description,
    tags: item.tags,
    confidential: item.confidential,
    confidentialAgents: item.confidentialAgents,
  }), []);

  // 搜索防抖
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPageNumber(1);
    }, 400);
  };

  const loadMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const params: { pageNumber?: number; pageSize?: number; title?: string; primaryCategory?: string; category?: string; modelCategory?: string; confidential?: number } = {
        pageNumber,
        pageSize,
      };
      if (debouncedSearch) params.title = debouncedSearch;
      if (filterPrimary !== 'all') params.primaryCategory = filterPrimary;
      if (filterCat !== 'all') params.category = filterCat;
      if (filterModel !== 'all') params.modelCategory = filterModel;
      if (filterLevel === 'confidential') params.confidential = 1;
      else if (filterLevel === 'normal') params.confidential = 0;
      const result = await listMaterial(params);
      setMaterials((result.list ?? []).map(toAdminMaterial));
      setTotal(result.total ?? 0);
    } catch (err) {
      console.error('加载资料列表失败', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterPrimary, filterCat, filterModel, filterLevel, pageNumber, pageSize, toAdminMaterial]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  // 独立加载各筛选级别下的资料总数，不受 filterLevel 影响
  const loadCounts = useCallback(async () => {
    try {
      const baseParams: { pageSize: number; title?: string; primaryCategory?: string; category?: string; modelCategory?: string } = {
        pageSize: 1,
      };
      if (debouncedSearch) baseParams.title = debouncedSearch;
      if (filterPrimary !== 'all') baseParams.primaryCategory = filterPrimary;
      if (filterCat !== 'all') baseParams.category = filterCat;
      if (filterModel !== 'all') baseParams.modelCategory = filterModel;

      const [resAll, resNormal, resConfidential] = await Promise.all([
        listMaterial(baseParams),
        listMaterial({ ...baseParams, confidential: 0 }),
        listMaterial({ ...baseParams, confidential: 1 }),
      ]);
      setCountAll(resAll.total ?? 0);
      setCountNormal(resNormal.total ?? 0);
      setCountConfidential(resConfidential.total ?? 0);
    } catch (err) {
      console.error('加载资料计数失败', err);
    }
  }, [debouncedSearch, filterPrimary, filterCat, filterModel]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTree();
        setTree(data);
      } catch (err) {
        console.error('加载分类树失败', err);
      }
    })();
  }, []);

  // 清理搜索防抖计时器
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // 从分类树构建 O(1) 查找映射，避免逐层遍历
  const categoryLookup = useMemo(() => {
    const subToPrimary = new Map<string, { id: string; label: string; icon: string; color: string }>();
    const subLabelMap = new Map<string, string>();
    const modelLabelMap = new Map<string, string>();
    for (const primary of tree) {
      for (const sub of primary.subCategories) {
        subToPrimary.set(sub.id, { id: primary.id, label: primary.label, icon: primary.icon, color: primary.color });
        subLabelMap.set(sub.id, sub.label);
        for (const model of sub.models) {
          modelLabelMap.set(model.id, model.label);
        }
      }
    }
    return { subToPrimary, subLabelMap, modelLabelMap };
  }, [tree]);

  const filtered = materials;

  // 分页计算
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIndex = (pageNumber - 1) * pageSize + 1;
  const endIndex = Math.min(pageNumber * pageSize, total);

  const goPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setPageNumber(page);
  };

  const pageNumbers: number[] = [];
  const maxVisible = 5;
  let pStart = Math.max(1, pageNumber - Math.floor(maxVisible / 2));
  let pEnd = Math.min(totalPages, pStart + maxVisible - 1);
  if (pEnd - pStart + 1 < maxVisible) {
    pStart = Math.max(1, pEnd - maxVisible + 1);
  }
  for (let i = pStart; i <= pEnd; i++) pageNumbers.push(i);

  const handleToggle = async (id: string) => {
    const m = materials.find((x) => x.id === id);
    if (!m || isDownloadLocked(m)) return;
    try {
      const result = await toggleDownload(id);
      setMaterials((prev) => prev.map((m) => m.id === id ? { ...m, downloadAllowed: !m.downloadAllowed } : m));
      showToast(result.downloadAllowed ? '已开启下载权限' : '已关闭下载权限');
    } catch (err) {
      showToast((err as Error).message || '切换下载开关失败', 'error');
    }
  };

  const handleToggleConfidential = async (id: string) => {
    try {
      const result = await toggleConfidential(id);
      setMaterials((prev) => prev.map((m) => {
        if (m.id !== id) return m;
        const newConf = result.confidential === 1;
        return { ...m, confidential: newConf, confidentialAgents: newConf ? (m.confidentialAgents ?? []) : [] };
      }));
    } catch (err) {
      console.error('切换机密状态失败', err);
    }
  };

  const handleSaveConfidentialAgents = async (materialId: string, agentIds: string[]) => {
    try {
      await saveConfidentialAgents(materialId, agentIds);
      setMaterials((prev) => prev.map((m) => m.id === materialId ? { ...m, confidentialAgents: agentIds } : m));
      showToast('机密授权已保存');
    } catch (err) {
      showToast((err as Error).message || '保存授权失败', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMaterial(id);
      setMaterials((prev) => prev.filter((m) => m.id !== id));
      setDeleteId(null);
    } catch (err) {
      console.error('删除资料失败', err);
    }
  };

  return (
    <AdminLayout title="资料管理" subtitle={`共 ${total} 份资料`}>

      <div className="flex items-center gap-3 mb-5">
        {[
          { key: 'all', label: '全部', count: countAll, icon: 'ri-folder-3-line', activeClass: 'border-gray-800 bg-gray-900 text-white', inactiveClass: 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50', countClass: 'bg-white/20 text-white' },
          { key: 'normal', label: '普通', count: countNormal, icon: 'ri-global-line', activeClass: 'border-gray-400 bg-gray-100 text-gray-800', inactiveClass: 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50', countClass: 'bg-gray-300 text-gray-700' },
          { key: 'confidential', label: '机密', count: countConfidential, icon: 'ri-shield-keyhole-line', activeClass: 'border-red-300 bg-red-50 text-red-700', inactiveClass: 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50', countClass: 'bg-red-100 text-red-600' },
        ].map(({ key, label, count, icon, activeClass, inactiveClass, countClass }) => (
          <button
            key={key}
            onClick={() => { setFilterLevel(key as 'all' | 'normal' | 'confidential'); setPageNumber(1); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${filterLevel === key ? activeClass : inactiveClass}`}
          >
            <i className={`${icon} text-sm`}></i>
            {label}
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${filterLevel === key ? countClass : 'bg-gray-100 text-gray-500'}`}>{count}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5 items-center">
        <div className="relative flex-1 max-w-sm">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm"></i>
          </div>
          <input type="text" value={search} onChange={(e) => handleSearchChange(e.target.value)} placeholder="搜索资料标题..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-400 bg-white" />
        </div>
        {/* 一级分类 */}
        <div className="relative">
          <select value={filterPrimary} onChange={(e) => { setFilterPrimary(e.target.value); setFilterCat('all'); setFilterModel('all'); setPageNumber(1); }} className="appearance-none pl-3 pr-10 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer">
            <option value="all">全部分类</option>
            {tree.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-arrow-down-s-line text-base"></i>
          </div>
        </div>
        {/* 二级分类 */}
        <div className="relative">
          <select value={filterCat} onChange={(e) => { setFilterCat(e.target.value); setFilterModel('all'); setPageNumber(1); }} className="appearance-none pl-3 pr-10 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer">
            <option value="all">全部二级分类</option>
            {filteredSubCategories.map((s) => <option key={s.id} value={s.id}>{s.primaryLabel} / {s.label}</option>)}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-arrow-down-s-line text-base"></i>
          </div>
        </div>
        {/* 三级分类（型号），仅在有型号时显示 */}
        {filteredModels.length > 0 && (
          <div className="relative">
            <select value={filterModel} onChange={(e) => { setFilterModel(e.target.value); setPageNumber(1); }} className="appearance-none pl-3 pr-10 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer">
              <option value="all">全部型号</option>
              {filteredModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-down-s-line text-base"></i>
            </div>
          </div>
        )}
        <button
          onClick={() => setShowCatManager(true)}
          className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-settings-3-line text-sm"></i>
          管理分类
        </button>
        <button
          onClick={() => navigate('/admin/materials/upload')}
          className="flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-colors cursor-pointer whitespace-nowrap ml-auto"
          style={{ background: '#0069BD' }}
        >
          <i className="ri-upload-cloud-line text-sm"></i>
          上传资料
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
            <span className="ml-2 text-sm text-gray-400">加载资料列表...</span>
          </div>
        ) : (<>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['资料', '一级 / 二级分类', '三级分类（型号）', '格式 / 级别', '上传日期', '浏览量', '下载量', '可下载', '操作'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((m) => {
                const locked = isDownloadLocked(m);
                return (
                  <tr key={m.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-8 rounded overflow-hidden bg-gray-100 shrink-0 relative">
                          {(() => {
                            const showPlaceholder = !m.thumbnail || brokenThumbnails.has(m.id);
                            if (showPlaceholder) {
                              const cfg = typeThumbnailConfig[m.type] ?? typeThumbnailConfig.PDF;
                              return (
                                <div className="w-full h-full flex items-center justify-center" style={{ background: cfg.bg }}>
                                  <i className={cfg.icon} style={{ color: cfg.color, fontSize: '16px' }}></i>
                                </div>
                              );
                            }
                            return (
                              <>
                                <img
                                  src={m.thumbnail}
                                  alt={m.title}
                                  className="w-full h-full object-cover"
                                  onError={() => handleThumbnailError(m.id)}
                                />
                                {m.isVideo && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <i className="ri-play-fill text-white text-xs"></i>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 max-w-[180px] truncate">{m.title}</p>
                          {m.videoDuration && <p className="text-xs text-gray-400 mt-0.5">{m.videoDuration}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const primaryFromSub = categoryLookup.subToPrimary.get(m.category);
                        const primaryById = tree.find((p) => p.id === m.primaryCategory);
                        const displayPrimary = primaryFromSub || primaryById;
                        const subLabel = categoryLookup.subLabelMap.get(m.category) || m.category;
                        return (
                          <div className="flex flex-col gap-1">
                            {displayPrimary ? (
                              <div className="flex items-center gap-1.5">
                                <span className="w-4 h-4 flex items-center justify-center rounded bg-gray-100 shrink-0">
                                  <i className={`${displayPrimary.icon} text-xs text-gray-500`}></i>
                                </span>
                                <span className="text-xs text-gray-500 whitespace-nowrap">{displayPrimary.label}</span>
                              </div>
                            ) : null}
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border font-medium whitespace-nowrap w-fit bg-gray-100 text-gray-600 border-gray-200">
                              {subLabel || '—'}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {m.modelCategory ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border font-medium whitespace-nowrap bg-gray-50 text-gray-600 border-gray-200">
                          {categoryLookup.modelLabelMap.get(m.modelCategory) || m.modelCategory}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5">
                        <TypeBadge type={m.type} />
                        <div className="flex items-center gap-1">
                          <ConfidentialBadge confidential={m.confidential} />
                          {m.confidential && (
                            <button onClick={() => setConfModal(m)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap" title="管理机密权限">
                              <div className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 border border-gray-200 hover:bg-gray-200">
                                <i className="ri-user-settings-line text-xs text-gray-500"></i>
                              </div>
                              <span className="text-xs text-gray-500 font-semibold">{(m.confidentialAgents ?? []).length}人</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{m.uploadDate || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span className="font-medium text-gray-800">{m.views.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {locked ? (
                        <span className="flex items-center gap-1 text-gray-400">
                          <i className="ri-eye-line text-xs"></i>
                          <span className="text-xs">仅浏览</span>
                        </span>
                      ) : (
                        <span className="font-medium text-gray-800">{m.downloads.toLocaleString()}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {locked ? (
                        <div className="flex items-center gap-1.5">
                          <span className="w-10 h-5 rounded-full bg-gray-200 relative inline-flex items-center opacity-50 cursor-not-allowed">
                            <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full"></span>
                          </span>
                          <span className="text-xs text-gray-400 whitespace-nowrap">仅浏览</span>
                        </div>
                      ) : (
                        <button onClick={() => handleToggle(m.id)} className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${m.downloadAllowed ? '' : 'bg-gray-300'}`} style={m.downloadAllowed ? { background: '#0069BD' } : {}}>
                          <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all duration-200" style={{ left: m.downloadAllowed ? '22px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}></span>
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <div className="relative group/tip">
                          <button onClick={() => handleToggleConfidential(m.id)} className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${m.confidential ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}>
                            <i className="ri-shield-keyhole-line text-sm"></i>
                          </button>
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
                            {m.confidential ? '改为普通' : '设为机密'}
                          </span>
                        </div>
                        <div className="relative group/tip">
                          <button onClick={() => navigate(`/admin/materials/${m.id}`)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer">
                            <i className="ri-eye-line text-sm"></i>
                          </button>
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">查看详情</span>
                        </div>
                        <div className="relative group/tip">
                          <button onClick={() => navigate(`/admin/materials/${m.id}/edit`)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer">
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">编辑</span>
                        </div>
                        <div className="relative group/tip">
                          <button onClick={() => setDeleteId(m.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">删除</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-16 text-center text-gray-400">
            <i className="ri-folder-open-line text-3xl mb-2 block"></i>
            <p className="text-sm">未找到资料</p>
          </div>
        )}

        {/* —— 分页 —— */}
        {total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/30">
            <span className="text-xs text-gray-500">
              第 {startIndex}-{endIndex} 条，共 {total} 条
            </span>
            <div className="flex items-center gap-2">
              {/* 每页条数 */}
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPageNumber(1); }}
                className="appearance-none pl-2 pr-7 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 focus:outline-none bg-white cursor-pointer"
              >
                {[10, 20, 50, 100].map((s) => (
                  <option key={s} value={s}>{s} 条/页</option>
                ))}
              </select>

              {/* 页码按钮 */}
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => goPage(pageNumber - 1)}
                  disabled={pageNumber <= 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>

                {pStart > 1 && (
                  <>
                    <button onClick={() => goPage(1)} className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer">1</button>
                    {pStart > 2 && <span className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">...</span>}
                  </>
                )}

                {pageNumbers.map((p) => (
                  <button
                    key={p}
                    onClick={() => goPage(p)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer"
                    style={p === pageNumber ? { background: '#0069BD', color: '#fff' } : { color: '#4b5563' }}
                  >
                    {p}
                  </button>
                ))}

                {pEnd < totalPages && (
                  <>
                    {pEnd < totalPages - 1 && <span className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">...</span>}
                    <button onClick={() => goPage(totalPages)} className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer">{totalPages}</button>
                  </>
                )}

                <button
                  onClick={() => goPage(pageNumber + 1)}
                  disabled={pageNumber >= totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          </div>
        )}
        </>)}
      </div>

      {confModal && (
        <ConfidentialAgentsModal material={confModal} onClose={() => setConfModal(null)} onSave={handleSaveConfidentialAgents} />
      )}

      {showCatManager && (
        <CategoryManagerModal onClose={() => setShowCatManager(false)} materials={materials} />
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-1">确认删除</h3>
            <p className="text-sm text-gray-500 text-center mb-5">此操作不可恢复，确认删除此资料？</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">删除</button>
            </div>
          </div>
        </div>
      )}
      {/* —— 操作提示 —— */}
      {toast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-[99999] flex items-center gap-2 px-4 py-3 bg-white border rounded-xl text-sm font-semibold ${toast.type === 'error' ? 'border-red-200 text-red-700' : 'border-green-200 text-green-700'}`}
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
        >
          <i className={`${toast.type === 'error' ? 'ri-error-warning-fill text-red-500' : 'ri-checkbox-circle-fill text-green-500'} text-base`}></i>
          {toast.message}
        </div>
      )}
    </AdminLayout>
  );
}